import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from "../../stores";
import { FamlaServices } from '../../services';
import { iAlert } from '../../interfaces';
import { InputForm, Spiner } from '../../components';
import { dedupeRoleOptions, type InviteRoleLike } from '../../utils/inviteRoleOptions';

interface ChildProps {
    processId: string;
    closeRef: any;
    interviewwId: string | undefined;
    invited: string | undefined;
    availableRoles?: ReadonlyArray<InviteRoleLike> | null;
    onReplaceSuccess?: () => void | Promise<void>;
}

/** Corps JSON renvoyé par l'API Famla (erreurs incluses) */
interface FamlaApiBody {
    status_code?: number;
    /** @deprecated faute de frappe historique — garder pour compatibilité */
    status_cose?: number;
    description?: string;
    response_type?: string;
}

/** Messages API connus → clés i18n (texte explicite traduit) */
const API_DESCRIPTION_I18N_KEYS: Record<string, string> = {
    'you cannot replace owner interview': 'views.processInterview.cannotReplaceOwner',
    'you cannot replace participant when the interview is finished': 'views.processInterview.cannotReplaceWhenInterviewFinished',
    'you cannot delete interview with existed mapp': 'views.processInterview.cannotDeleteMappedInterview',
    'process cannot be update by its user': 'views.processInterview.processUpdateRestricted',
};

const ReplaceModal = ({
    processId,
    closeRef,
    interviewwId,
    invited,
    availableRoles,
    onReplaceSuccess,
}: ChildProps) => {
    const { t } = useTranslation();
    const users = useAppSelector(state => state.auth);
    const [email, setEmail] = useState('');
    const [isSubmit, setIsSubmit] = useState(false);
    const [alert, setAlert] = useState<iAlert>({ isActive: false, type: '', message: '' });
    const [role, setRole] = useState(invited || "");
    const roleOptions = useMemo(() => dedupeRoleOptions(availableRoles), [availableRoles]);

    useEffect(() => {
        setRole(invited || '');
    }, [invited]);

    const showDangerAlert = (message: string) => {
        setAlert({ isActive: true, type: 'danger', message });
        setTimeout(() => {
            setAlert({ isActive: false, type: '', message: "" });
        }, 8000);
    };

    const resolveReplaceErrorMessage = (
        httpStatus: number,
        body: FamlaApiBody | undefined
    ): string => {
        const apiCode = body?.status_code ?? body?.status_cose;
        const rawDesc = typeof body?.description === 'string' ? body.description.trim() : '';

        if (rawDesc) {
            const i18nKey = API_DESCRIPTION_I18N_KEYS[rawDesc];
            if (i18nKey) {
                return t(i18nKey);
            }
            return rawDesc;
        }

        if (httpStatus === 401 || apiCode === 401) {
            return t('navigation.youAreNotLoggedIn');
        }
        if ((httpStatus === 404 || apiCode === 404) && body?.response_type === 'interview not found') {
            return t('views.processInterview.interviewNotFound');
        }
        if ((httpStatus === 404 || apiCode === 404) && body?.response_type === 'processus not found') {
            return t('views.processInterview.processNotFound');
        }

        return t('navigation.anErrorOccurred');
    };

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(event.target.value);
    };

    const handleRoleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRole(event.target.value);
    };

    const handleSubmit = async (event: React.SyntheticEvent) => {
        event.preventDefault();
        setIsSubmit(true);

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email.trim() || !emailRegex.test(email)) {
            setAlert({ isActive: true, type: 'danger', message: t('navigation.enterValideEmail') });
            setIsSubmit(false);
            return;
        }

        let newFormData = {
            mail: email,
            role: role || invited
        };

        let data = await FamlaServices("api_host", `/interview/add_member/${interviewwId}`, "PUT", newFormData, users.access_token);

        if (data.status === 200) {
            setAlert({ isActive: true, type: 'success', message: t('views.processInterview.memberAdded') });
            setEmail('');
            setRole("");
            if (onReplaceSuccess) {
                await onReplaceSuccess();
                closeRef?.current?.click();
            } else {
                window.location.reload();
            }
        } else {
            const body = data.body as FamlaApiBody | undefined;
            const message = resolveReplaceErrorMessage(data.status, body);
            showDangerAlert(message);
        }
        setIsSubmit(false);
    };

    return (
        <div className="modal" data-bs-backdrop="static" id='replaceModal' tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content bg-dark">
                    <div className="modal-header border-0">
                        <h5 className="modal-title text-white">{t('words.replace')}</h5>
                        <button type="button" className="btn-close bg-white" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        {alert.isActive && <div className={`alert alert-${alert.type}`} role="alert">{alert.message}</div>}

                        <div className="mb-3">
                            <InputForm
                                inputType="email"
                                inputLabel={t('words.email')}
                                inputName="email"
                                inputValue={email}
                                isFloatingLabel={true}
                                labelClass="text-theme"
                                onChange={handleEmailChange}
                                inputClass="bg-dark text-light"
                            />
                        </div>

                        {roleOptions.length > 0 ? (
                            <>
                                <div className="mb-3">
                                    <select
                                        className="form-select bg-dark text-theme py-3"
                                        value={roleOptions.some((o) => o.role === role) ? role : ''}
                                        onChange={(e) => setRole(e.target.value)}
                                        aria-label={t('navigation.selectRole')}
                                    >
                                        <option className="text-theme" value="">{t('navigation.selectRole')}</option>
                                        {roleOptions.map((option) => (
                                            <option key={option.role} value={option.role}>
                                                {option.role}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="d-flex align-items-center my-2 gap-1">
                                    <hr className="flex-grow-1 border-secondary" />
                                    <span className="mx-2 text-light">{t('words.or')}</span>
                                    <hr className="flex-grow-1 border-secondary" />
                                </div>
                            </>
                        ) : null}

                        <InputForm
                            inputType="text"
                            inputLabel={t('navigation.enterRole')}
                            inputName="role"
                            inputValue={role || invited}
                            isFloatingLabel={true}
                            labelClass="text-theme"
                            onChange={handleRoleChange}
                            inputClass="bg-dark text-theme"
                        />
                    </div>
                    <div className="modal-footer border-secondary">
                        <button ref={closeRef} type="button" className="btn btn-secondary" data-bs-dismiss="modal">{t('words.close')}</button>
                        <button type="button" onClick={handleSubmit} className="btn btn-primary">
                            {isSubmit ? <Spiner className='spinner-border-sm' /> : <>{t('words.replace')}</>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReplaceModal;
